import { redirect } from 'react-router-dom'
import { checkSubscriptionStatus } from './api'
import pRetry from 'p-retry'
import { errAsync, ResultAsync } from 'neverthrow'
import {
  CookieExpiredError,
  FailedToFetchUserSubscriptionStatusError,
  MissingAuthTokenError,
  OfficeIsNotReadyError,
  UserNotSubscribedError,
} from './errors'
import {
  checkMSAuthToken,
  fetchSSOToken,
  setTokenToCookie,
} from './utils/cookies'

const waitUntilOfficeReady = () =>
  new Promise<void>((resolve, reject) => {
    const id = setTimeout(() => {
      reject(new OfficeIsNotReadyError())
    }, 10000) // 10 seconds timeout
    Office.onReady(() => {
      clearTimeout(id)
      resolve()
    })
  })

const waitUntilOfficeReadyWrapper = () =>
  ResultAsync.fromPromise(waitUntilOfficeReady(), (error) =>
    error instanceof OfficeIsNotReadyError ? error : new OfficeIsNotReadyError()
  )

const checkSubscription = () =>
  ResultAsync.fromPromise(
    (async () => {
      const subscribed = await pRetry(() => checkSubscriptionStatus(), {
        retries: 5,
        minTimeout: 1000,
        maxTimeout: 10000,
      })

      if (!subscribed.isSubscribed) {
        throw new UserNotSubscribedError()
      }
    })(),
    (err) =>
      err instanceof UserNotSubscribedError
        ? err
        : new FailedToFetchUserSubscriptionStatusError()
  )

export const subscriptionStatusLoader = async () => {
  const result = await waitUntilOfficeReadyWrapper()
    .andThen(checkMSAuthToken)
    .orElse((err) => {
      if (
        err instanceof CookieExpiredError ||
        err instanceof MissingAuthTokenError
      )
        return fetchSSOToken()
      return errAsync(err)
    })
    .andThen(setTokenToCookie)
    .andThen(() => checkSubscription())
  if (result.isErr()) {
    switch (result.error._tag) {
      case 'InvalidJwtTokenError':
        throw result.error
      case 'SetExpiredCookieError':
        throw redirect('/auth')
      case 'FetchTokenFailedError':
        throw redirect('/auth')
      case 'UserNotSubscribedError':
        throw redirect('/unsubscribed')
      case 'OfficeIsNotReadyError':
        throw result.error
      case 'FailedToFetchUserSubscriptionStatusError':
        throw result.error
      default:
        throw result.error
    }
  }

  return null
}
